<script setup>
import { ref, computed, watch } from "vue";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuLabel,
} from "@/shared/ui/dropdown-menu";
import { getBlockIcon } from "@/shared/ui/tiptap/blockIcons";
import ColorTextPopover from "./toolbar/ColorTextPopover.vue";
import { useEditorMenuState } from "../composables/useEditorMenuState";

const props = defineProps({
  editor: { type: Object, required: true },
  node: { type: Object, default: null },
  pos: { type: Number, default: 0 },
  visible: { type: Boolean, default: false },
});

const emit = defineEmits(["lock", "unlock"]);

const menuState = useEditorMenuState();

const menuOpen = ref(false);
const showTooltip = ref(false);
let tooltipTimer = null;

// Position of the last paragraph added via "+", so repeated clicks chain downward
const lastInsertPos = ref(null);

watch([() => props.pos, () => props.visible], () => {
  lastInsertPos.value = null;
});

function addBlockBelow() {
  const editor = props.editor;

  let basePos = null;
  if (lastInsertPos.value != null) {
    const prev = editor.state.doc.nodeAt(lastInsertPos.value);
    if (prev) basePos = lastInsertPos.value + prev.nodeSize;
  }
  if (basePos == null) {
    if (!props.node || props.pos == null) return;
    basePos = props.pos + props.node.nodeSize;
  }

  editor
    .chain()
    .insertContentAt(basePos, { type: "paragraph" })
    .setTextSelection(basePos + 1)
    .focus()
    .run();

  lastInsertPos.value = basePos;
  emit("unlock");
}

// --- Current block type label ---
const blockTypeLabel = computed(() => {
  if (!props.node) return "";
  const type = props.node.type.name;
  if (type === "heading") return `заголовок ${props.node.attrs.level}`;
  if (type === "bulletList") return "список";
  if (type === "orderedList") return "нум. список";
  if (type === "blockquote") return "цитата";
  if (type === "codeBlock") return "код";
  if (type === "table") return "таблица";
  return "параграф";
});

// --- Turn Into options ---
const turnIntoItems = [
  {
    label: "Параграф",
    iconKey: "paragraph",
    action: () => props.editor.chain().focus().setParagraph().run(),
  },
  {
    label: "Заголовок 1",
    iconKey: "h1",
    action: () =>
      props.editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    label: "Заголовок 2",
    iconKey: "h2",
    action: () =>
      props.editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    label: "Заголовок 3",
    iconKey: "h3",
    action: () =>
      props.editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    label: "Маркированный список",
    iconKey: "bullet-list",
    action: () => props.editor.chain().focus().toggleBulletList().run(),
  },
  {
    label: "Нумерованный список",
    iconKey: "ordered-list",
    action: () => props.editor.chain().focus().toggleOrderedList().run(),
  },
  {
    label: "Цитата",
    iconKey: "quote",
    action: () => props.editor.chain().focus().toggleBlockquote().run(),
  },
  {
    label: "Блок кода",
    iconKey: "code",
    action: () => props.editor.chain().focus().toggleCodeBlock().run(),
  },
];

// --- Hover / Lock ---
function onRowEnter() {
  emit("lock");
}

function onRowLeave() {
  if (!menuOpen.value) {
    emit("unlock");
  }
  clearTimeout(tooltipTimer);
  showTooltip.value = false;
}

function onGripEnter() {
  if (menuOpen.value) return;
  tooltipTimer = setTimeout(() => {
    showTooltip.value = true;
  }, 600);
}

function onGripLeave() {
  clearTimeout(tooltipTimer);
  showTooltip.value = false;
}

function onMenuToggle(open) {
  menuOpen.value = open;
  menuState.blockMenuOpen = open;
  if (open) {
    emit("lock");
    showTooltip.value = false;
    clearTimeout(tooltipTimer);
  } else {
    emit("unlock");
  }
}

// --- Actions ---
function duplicateBlock() {
  if (!props.node || props.pos == null) return;
  const endPos = props.pos + props.node.nodeSize;
  const slice = props.editor.state.doc.slice(props.pos, endPos);
  props.editor
    .chain()
    .focus()
    .insertContentAt(endPos, slice.content.toJSON())
    .run();
  menuOpen.value = false;
}

function copyToClipboard() {
  if (!props.node) return;
  const text = props.node.textContent;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text);
  } else {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
  menuOpen.value = false;
}

function deleteBlock() {
  if (!props.node || props.pos == null) return;
  props.editor
    .chain()
    .focus()
    .deleteRange({ from: props.pos, to: props.pos + props.node.nodeSize })
    .run();
  menuOpen.value = false;
}

// --- Drag and Drop ---
function onDragStart(e) {
  if (!props.node || props.pos == null) return;
  e.dataTransfer.effectAllowed = "move";
  e.dataTransfer.setData("text/plain", "");
  // Store drag info on editor for drop handler
  props.editor._dragFromPos = props.pos;
  props.editor._dragNodeSize = props.node.nodeSize;
  emit("lock");
}

function onDragEnd() {
  props.editor._dragFromPos = null;
  props.editor._dragNodeSize = null;
  emit("unlock");
}
</script>

<template>
  <div
    class="drag-handle-row"
    :class="visible ? 'is-visible' : ''"
    @mouseenter="onRowEnter"
    @mouseleave="onRowLeave"
  >
    <!-- + Add new line below -->
    <button
      type="button"
      class="drag-add-btn"
      title="Добавить строку"
      @click="addBlockBelow"
    >
      <svg
        class="w-3.5 h-3.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
      >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    </button>

    <!-- ⠿ Grip / Options -->
    <DropdownMenu @update:open="onMenuToggle">
      <DropdownMenuTrigger as-child>
        <button
          type="button"
          class="drag-grip-btn"
          draggable="true"
          @mouseenter="onGripEnter"
          @mouseleave="onGripLeave"
          @dragstart="onDragStart"
          @dragend="onDragEnd"
        >
          <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="9" cy="5" r="1.5" />
            <circle cx="15" cy="5" r="1.5" />
            <circle cx="9" cy="12" r="1.5" />
            <circle cx="15" cy="12" r="1.5" />
            <circle cx="9" cy="19" r="1.5" />
            <circle cx="15" cy="19" r="1.5" />
          </svg>
        </button>
      </DropdownMenuTrigger>

      <!-- Tooltip -->
      <Transition name="drag-tooltip">
        <div v-if="showTooltip && !menuOpen" class="drag-tooltip">
          Нажмите для опций, удерживайте для перетаскивания
        </div>
      </Transition>

      <!-- Context Menu -->
      <DropdownMenuContent
        side="right"
        :side-offset="8"
        align="start"
        class="editor-block-menu w-56"
      >
        <!-- Block type label -->
        <DropdownMenuLabel>
          {{ blockTypeLabel }}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <!-- Color -->
        <ColorTextPopover :editor="editor">
          <button class="editor-menu-item">
            <span class="editor-menu-icon">
              <svg
                class="w-[18px] h-[18px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="4" fill="currentColor" />
              </svg>
            </span>
            <span class="editor-menu-text">{{ $t("Цвет") }}</span>
          </button>
        </ColorTextPopover>

        <!-- Turn Into submenu -->
        <DropdownMenuSub>
          <DropdownMenuSubTrigger class="editor-menu-subtrigger">
            <span class="editor-menu-icon">
              <svg
                class="w-[18px] h-[18px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M4 7V4h16v3" />
                <path d="M9 20h6" />
                <path d="M12 4v16" />
              </svg>
            </span>
            <span class="editor-menu-text">Превратить в</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent class="editor-block-menu w-56">
            <DropdownMenuItem
              v-for="item in turnIntoItems"
              :key="item.label"
              @click="item.action()"
            >
              <span
                class="editor-menu-icon"
                v-html="getBlockIcon(item.iconKey)"
              />
              <span class="editor-menu-text">{{ item.label }}</span>
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <!-- Duplicate -->
        <DropdownMenuItem @click="duplicateBlock">
          <span class="editor-menu-icon">
            <svg
              class="w-[18px] h-[18px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path
                d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
              />
            </svg>
          </span>
          <span class="editor-menu-text">Дублировать</span>
          <span class="editor-menu-kbd">⌘D</span>
        </DropdownMenuItem>

        <!-- Copy to clipboard -->
        <DropdownMenuItem @click="copyToClipboard">
          <span class="editor-menu-icon">
            <svg
              class="w-[18px] h-[18px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
              />
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
            </svg>
          </span>
          <span class="editor-menu-text">Скопировать</span>
          <span class="editor-menu-kbd">⌘C</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <!-- Delete -->
        <DropdownMenuItem @click="deleteBlock" class="text-destructive">
          <span class="editor-menu-icon">
            <svg
              class="w-[18px] h-[18px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path
                d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
              />
            </svg>
          </span>
          <span class="editor-menu-text">Удалить</span>
          <span class="editor-menu-kbd">Del</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>

<style scoped>
.drag-handle-row {
  @apply absolute flex items-center gap-0.5;
  left: -68px;
  top: 2px;
  opacity: 0;
  transform: translateX(6px);
  transition: opacity 150ms ease, transform 150ms ease,
    top 160ms cubic-bezier(0.22, 1, 0.36, 1);
  pointer-events: none;
}

.drag-handle-row.is-visible {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
}

.drag-add-btn,
.drag-grip-btn {
  @apply w-6 h-6 flex items-center justify-center rounded;
  @apply text-muted-foreground;
  transition: background 150ms ease, color 150ms ease, transform 100ms ease;
}

.drag-add-btn:hover,
.drag-grip-btn:hover {
  @apply bg-muted text-foreground;
}

.drag-add-btn:active,
.drag-grip-btn:active {
  transform: scale(0.9);
}

.drag-grip-btn {
  @apply cursor-grab;
}

.drag-grip-btn:active {
  @apply cursor-grabbing;
}

.drag-tooltip {
  @apply absolute top-full mt-1 left-0;
  @apply px-2 py-1 rounded text-xs whitespace-nowrap;
  @apply bg-gray-900 text-white pointer-events-none;
  z-index: 50;
}

.drag-tooltip-enter-active {
  transition: opacity 150ms ease;
}

.drag-tooltip-leave-active {
  transition: opacity 100ms ease;
}

.drag-tooltip-enter-from,
.drag-tooltip-leave-to {
  opacity: 0;
}
</style>

<style>
/* Shared editor menu look — matches slash command popup */
.editor-block-menu,
.editor-menu-box {
  border-radius: var(--radius-xl);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
}

.editor-block-menu {
  padding: 0.5em;
}

.editor-block-menu [data-slot="dropdown-menu-label"] {
  padding: 0.5em 0.625em;
  font-size: 13px;
  font-weight: 700;
  color: hsl(var(--foreground));
}

.editor-block-menu [data-slot="dropdown-menu-separator"] {
  margin: 0.375em 0.5em;
}

.editor-block-menu [data-slot="dropdown-menu-item"],
.editor-block-menu .editor-menu-item,
.editor-block-menu .editor-menu-subtrigger {
  gap: 0.625em;
  padding: 0.5em;
  border-radius: 6px;
}

.editor-block-menu .editor-menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  transition: background 100ms ease;
}

.editor-block-menu .editor-menu-item:hover {
  background: hsl(var(--muted));
}

/* Group label inside Add Block popover */
.editor-menu-group {
  padding: 0.5em 0.625em;
  font-size: 13px;
  font-weight: 700;
  color: hsl(var(--foreground));
}

/* Flat icon — 1.5em box, 18px glyph */
.editor-menu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5em;
  height: 1.5em;
  flex-shrink: 0;
  color: hsl(var(--muted-foreground));
}

.editor-menu-icon svg {
  width: 18px !important;
  height: 18px !important;
}

.editor-menu-text {
  font-size: 15px;
  line-height: 1.3;
  white-space: nowrap;
}

.editor-menu-kbd {
  margin-left: auto;
  font-size: 13px;
  color: hsl(var(--muted-foreground));
}
</style>
